import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { coupleAuth } from '../../services/api';
import { UserPlus, Mail, Lock, Eye, EyeOff, Heart, Users, Link as LinkIcon, Check, X, Loader } from 'lucide-react';

function CoupleRegister() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    partner1_name: '',
    partner2_name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [slug, setSlug] = useState('');
  const [customSlug, setCustomSlug] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generateSlug = useCallback((name1, name2) => {
    if (!name1 || !name2) return '';
    const clean = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${clean(name1)}and${clean(name2)}`;
  }, []);

  // Auto-generate slug from partner names
  useEffect(() => {
    if (!customSlug) {
      const generated = generateSlug(formData.partner1_name, formData.partner2_name);
      setSlug(generated);
    }
  }, [formData.partner1_name, formData.partner2_name, customSlug, generateSlug]);

  // Check slug availability with debounce
  useEffect(() => {
    if (!slug || slug.length < 3) {
      setSlugStatus(null);
      return;
    }

    setSlugStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const response = await coupleAuth.checkSlug(slug);
        setSlugStatus(response.data.available ? 'available' : 'taken');
      } catch {
        setSlugStatus(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [slug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(value);
    setCustomSlug(true);
  };

  const resetSlug = () => {
    setCustomSlug(false);
    setSlug(generateSlug(formData.partner1_name, formData.partner2_name));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (slugStatus === 'taken') {
      setError('Please choose a different wedding page URL.');
      return;
    }

    setLoading(true);

    try {
      const response = await coupleAuth.register({
        partner1_name: formData.partner1_name,
        partner2_name: formData.partner2_name,
        email: formData.email,
        password: formData.password,
        slug
      });

      localStorage.setItem('coupleToken', response.data.token);
      localStorage.setItem('coupleUser', JSON.stringify(response.data.couple));

      navigate('/couple/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-100 via-pink-50 to-rose-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-4">
              <Heart className="w-8 h-8 text-rose-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Create Your Wedding Page</h2>
            <p className="mt-2 text-gray-600">
              Start planning your perfect day together
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Partner Names */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Partner 1
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Users className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="partner1_name"
                    value={formData.partner1_name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                    placeholder="First name"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Partner 2
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Users className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="partner2_name"
                    value={formData.partner2_name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                    placeholder="First name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Slug Preview */}
            {slug && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Wedding Page URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LinkIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={slug}
                    onChange={handleSlugChange}
                    className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 ${
                      slugStatus === 'taken' ? 'border-red-300 bg-red-50' :
                      slugStatus === 'available' ? 'border-green-300 bg-green-50' :
                      'border-gray-300'
                    }`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {slugStatus === 'checking' && <Loader className="h-5 w-5 text-gray-400 animate-spin" />}
                    {slugStatus === 'available' && <Check className="h-5 w-5 text-green-600" />}
                    {slugStatus === 'taken' && <X className="h-5 w-5 text-red-600" />}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className={`text-xs ${
                    slugStatus === 'taken' ? 'text-red-600' :
                    slugStatus === 'available' ? 'text-green-600' :
                    'text-gray-500'
                  }`}>
                    {slugStatus === 'taken' && 'This URL is already taken'}
                    {slugStatus === 'available' && 'This URL is available!'}
                    {slugStatus === 'checking' && 'Checking availability...'}
                    {!slugStatus && `bridalconnect.co.za/${slug}`}
                  </p>
                  {customSlug && (
                    <button
                      type="button"
                      onClick={resetSlug}
                      className="text-xs text-rose-600 hover:text-rose-700 font-medium"
                    >
                      Reset to auto
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                  placeholder="Confirm your password"
                  required
                  minLength={6}
                />
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading || slugStatus === 'taken'}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-rose-600 hover:to-pink-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating your page...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Wedding Page
                </span>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Already have an account?</span>
              </div>
            </div>
          </div>

          {/* Login link */}
          <div className="mt-6">
            <Link
              to="/couple/login"
              className="w-full flex justify-center py-3 px-4 border-2 border-rose-300 text-rose-600 rounded-lg hover:bg-rose-50 transition-all font-medium"
            >
              Sign In Instead
            </Link>
          </div>
        </div>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-rose-600 transition-colors"
          >
            &larr; Back to directory
          </Link>
        </div>
      </div>
    </div>
  );
}

export default CoupleRegister;
